<template>
  <div>

    <!-- Hero Section -->
    <HeroSection
      title="Discover Your Perfect Style"
      description="Shop the latest fashion trends and find your perfect look. Free shipping on orders over $250!"
      :primary-action="{ label: 'Shop Now', to: '/category/footwear' }"
      :secondary-action="{ label: 'Explore Collection', to: '/' }"
      image="https://picsum.photos/seed/hero-fashion/800/600"
      image-alt="Fashion Collection"
    />

    <!-- Popular Products -->
    <PopularProducts />

    <!-- Features Section -->
    <section class="container mx-auto px-6 py-8">
      <div class="grid grid-cols-2 md:grid-cols-4 gap-6">
        <FeatureCard
          icon="i-heroicons-truck"
          title="Free Shipping!"
          description="Orders over $199"
        />
        <FeatureCard
          icon="i-heroicons-credit-card"
          title="Secure Payment"
          description="Safe transactions"
        />
        <FeatureCard
          icon="i-heroicons-arrow-path"
          title="Easy Returns"
          description="30 day guarantee"
        />
        <FeatureCard
          icon="i-heroicons-chat-bubble-left-right"
          title="24/7 Support"
          description="Always here to help"
        />
      </div>
    </section>

    <!-- Featured Categories -->
    <section class="container mx-auto px-6 py-12">
      <h2 class="text-3xl font-bold mb-8 text-center text-gray-900 dark:text-gray-100">{{ $t('Shop by category') }}</h2>
      <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
        <CategoryCard
          v-for="category in randomCategories"
          :key="category.id"
          :title="category.name"
          :image="`https://picsum.photos/seed/${category.slug}/800/600`"
          :to="$localePath({ name: 'category-slug', params: { slug: category.slug } })"
        />
      </div>
    </section>

    <!-- Highlight Products Section -->
    <HighlightProducts :products="highlightProducts" />

    <!-- Customer Reviews Section -->
     <section class="bg-elevated py-12">
      <UContainer>
          <h2 class="text-3xl font-bold mb-8 text-center text-gray-900 dark:text-white">
            What Our Customers Say
          </h2>

          <div class="px-4 lg:px-0">
            <ReviewsCarousel />
          </div>
      </UContainer>
    </section>


    <PartnerLogos />
  </div>
</template>

<script setup lang="ts">
import FeatureCard from "~/components/molecules/FeatureCard.vue"
import CategoryCard from "~/components/molecules/CategoryCard.vue"
import HeroSection from "~/components/organisms/HeroSlider.vue"
import PopularProducts from "~/components/organisms/PopularProducts.vue"
import HighlightProducts from "~/components/organisms/HighlightProducts.vue"
import ReviewsCarousel from "~/components/organisms/ReviewsCarousel.vue"
import PartnerLogos from '~/components/organisms/PartnerLogos.vue'
import type { ApiCollectionResponse, Product } from '~/types';

const categoryStore = useCategoryStore();

if (!categoryStore.loaded) {
  await categoryStore.load();
}

const randomCategories = computed(() => {
  const categories = categoryStore.categories;
  const shuffled = categories.sort(() => 0.5 - Math.random());
  return shuffled.slice(0, 3);
});

definePageMeta({ layout: 'shop' })

const {
  locale
} = useI18n();

// Use SSR-compatible data fetching
const { data, pending, error } = await useFetch<ApiCollectionResponse<Product>>(
    '/api/v1/products?highlights=true',
    {
      query: { per_page: 8, highlight: true },
      key: 'highlight-products',
      watch: [locale],
      default: () => ({ data: [] })
    }
)

const highlightProducts = computed(() => data.value.data)
</script>
